import type { ReactNode } from 'react'
import { ApiError, type AccessApprovalCard, type AccessExtraScope, type AccessPersonPage, type AccessPersonStatus, type ApiFailure } from '../api/types'
import { Badge } from '../components/ui'

/*
 * THE ACCESS MAP'S WORDS, shared by its screens (People, the person's page,
 * Approvals): one badge per status, one tag per warning severity, one way to say a
 * date. A badge or a tag that two screens spelled differently would be two answers
 * to one question.
 */

type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'privileged'

export const STATUS: Record<AccessPersonStatus, { label: string; tone: Tone }> = {
  CAN_ENTER: { label: 'Can enter', tone: 'good' },
  NO_SIGN_IN: { label: 'Approved, not signed in yet', tone: 'good' },
  WAITING: { label: 'Waiting for approval', tone: 'warn' },
  RETURNED: { label: 'Back after leaving: look again', tone: 'warn' },
  WIDENED: { label: 'Gained more: look again', tone: 'warn' },
  BAD_EMAIL: { label: 'No company email', tone: 'warn' },
  ROLE_NOT_SET: { label: 'HR Role not configured', tone: 'neutral' },
  NO_ROLE: { label: 'No HR Role', tone: 'neutral' },
  PLACEHOLDER: { label: 'Placeholder account', tone: 'neutral' },
  LEFT: { label: 'Left', tone: 'neutral' },
  DISABLED: { label: 'Disabled', tone: 'neutral' },
  SEES_ALL: { label: 'Full data access', tone: 'privileged' },
}

/** The order the badges sort in: what needs an admin first. */
export const STATUS_ORDER: AccessPersonStatus[] = [
  'WAITING', 'RETURNED', 'WIDENED', 'BAD_EMAIL', 'ROLE_NOT_SET', 'NO_ROLE', 'NO_SIGN_IN', 'CAN_ENTER',
  'SEES_ALL', 'PLACEHOLDER', 'DISABLED', 'LEFT',
]

export function StatusBadge({ status }: { status: AccessPersonStatus }) {
  const s = STATUS[status] ?? { label: status, tone: 'neutral' as const }
  return <Badge tone={s.tone}>{s.label}</Badge>
}

/**
 * How serious a warning is (05 section 7.5): WIDER ROWS first and in red -- they
 * would see rows that are probably not theirs.
 */
export const SEVERITY: Record<string, { label: string; tone: Tone }> = {
  WIDER_ROWS: { label: 'Wider rows', tone: 'bad' },
  BLOCKS_ENTRY: { label: 'Cannot enter', tone: 'warn' },
  NO_ROWS: { label: 'No rows', tone: 'warn' },
  FEWER_ROWS: { label: 'Fewer rows', tone: 'warn' },
  INFO: { label: 'Information', tone: 'neutral' },
}

export function SeverityTag({ severity }: { severity: string }) {
  const s = SEVERITY[severity] ?? { label: severity.replace(/_/g, ' ').toLowerCase(), tone: 'neutral' as const }
  return <Badge tone={s.tone}>{s.label}</Badge>
}

/**
 * The approve button names what the approver accepts (7.5): "Approve anyway: 1
 * widens their rows" when a warning widens, "Approve (3 problems)" otherwise.
 */
export function approveLabel(warnings: { severity: string }[]): string {
  const widens = warnings.filter((w) => w.severity === 'WIDER_ROWS').length
  if (widens > 0) return `Approve anyway: ${widens} ${widens === 1 ? 'widens' : 'widen'} their rows`
  if (warnings.length > 0) return `Approve (${warnings.length} ${warnings.length === 1 ? 'problem' : 'problems'})`
  return 'Approve'
}

export const HIERARCHY: Record<number, string> = { 0: 'Off', 1: 'Type 1', 2: 'Type 2' }

/** Why the door says no, in words (owner, 28 Oct 2026: "HR role", not "role"; 30 Oct: "configured", not "set"). */
export const ENTRY_REASON: Record<string, string> = {
  DENIED_ROLE_NOT_SET: 'HR Role not configured',
  DENIED_NO_HR_ROLE: 'no HR Role in Mindzer',
  DENIED_NOT_APPROVED: 'not approved yet',
  DENIED_WITHDRAWN: 'approval taken back',
  DENIED_DISABLED: 'disabled in Mindzer',
  DENIED_LEFT: 'left the company',
  DENIED_NOT_IN_HR: 'not in Mindzer’s list',
  DENIED_BAD_EMAIL: 'no usable company email',
  DENIED_PLACEHOLDER: 'placeholder or test account',
  DENIED_NO_SIGN_IN: 'not signed in to MBI yet',
  DENIED_RETURNED: 'left and came back since their approval',
  DENIED_WIDENED: 'an update gave them more than their approval covered',
  DENIED_NOT_BUILT: 'their access is still being prepared',
}

/**
 * A refusal that an approval would end: the person is waiting, not shut out (S17).
 * WAITING is the Login As list's word for Development, who is not told why (F19).
 */
export const WAITING_FOR_APPROVAL = new Set(['WAITING', 'DENIED_NOT_APPROVED', 'DENIED_RETURNED', 'DENIED_WIDENED'])

/** A refusal code in words, for a code with no entry above. */
export function entryReason(code: string): string {
  return ENTRY_REASON[code] ?? code.replace(/^DENIED_/, '').replace(/_/g, ' ').toLowerCase()
}

export const SYSTEM_ROLE: Record<string, string> = {
  SystemMasterAdmin: 'System Master Admin',
  SystemAdmin: 'System Admin',
  SystemDevelopment: 'System Development',
  SystemMasterReader: 'System Master Reader',
}

export function roleLabel(p: { roleName: string | null; roleFunction?: string | null }): string {
  return [p.roleName, p.roleFunction].filter(Boolean).join(' - ')
}

/* One formatter each, made once: toLocaleDateString builds a new one on every call, and the
   Users list called it for every row it drew (29 Oct 2026). The same words; an invalid date
   still reads "Invalid Date", where format() would throw. */
const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' })
const DAY_TIME = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export function day(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'Invalid Date' : DAY.format(d)
}

export function dayTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'Invalid Date' : DAY_TIME.format(d)
}

export function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  // A time ahead of this clock (a skewed browser) is shown as the date, never "Today".
  if (ms < 0) return day(iso)
  const days = Math.floor(ms / 86_400_000)
  return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 30 ? `${days} days ago` : day(iso)
}

/** A length of time as the sync screens say it: minutes, then hours, then days. */
export function ageWords(minutes: number): string {
  const unit = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`
  return minutes < 60 ? unit(minutes, 'minute') : minutes < 48 * 60 ? unit(Math.round(minutes / 60), 'hour') : unit(Math.round(minutes / 1440), 'day')
}

export function listWords(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** A person's additional scopes, each its lines (a unit or team each) in read order (owner, 29 Oct 2026). */
export function extraScopesOf(page: AccessPersonPage): Map<number, AccessExtraScope[]> {
  const byGroup = new Map<number, AccessExtraScope[]>()
  for (const x of page.extras) byGroup.set(x.scopeGroup ?? x.extraScopeId, [...(byGroup.get(x.scopeGroup ?? x.extraScopeId) ?? []), x])
  return byGroup
}

export function failureOf(e: unknown): ApiFailure {
  return e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }
}

/** A refusal's sentence for a notice: the server's own words where it sent some. */
export function messageOf(e: unknown): string {
  const f = failureOf(e)
  return 'title' in f && f.title ? f.title : f.kind === 'not-found' ? 'Not found: reload the list.' : 'Something went wrong. Try again.'
}

/**
 * A filter with its name ABOVE it, as the report filter bars set theirs
 * (ReceivablesAgingPage's Pick): the name reads as the control's title, not as
 * a word beside it (owner, 27 Oct 2026: "the filters are not clear").
 */
export function FilterSlot({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`flex min-w-[150px] flex-1 flex-col gap-1 ${wide ? 'max-w-[320px]' : 'max-w-[230px]'}`}>
      <span className="eyebrow">{label}</span>
      {children}
    </div>
  )
}

/** An on/off filter as a pill that says its state, in place of a bare checkbox. */
export function ToggleChip({ on, onChange, children }: { on: boolean; onChange: (on: boolean) => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onChange(!on)}
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-(length:--text-small) font-medium transition-colors ${
        on
          ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white hover:bg-(--color-navy-800)'
          : 'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) hover:border-(--color-navy-300) hover:bg-(--color-navy-50) hover:text-(--color-ink)'
      }`}
    >
      <span aria-hidden className={`grid h-3.5 w-3.5 place-items-center rounded-full border text-[9px] leading-none ${
        on ? 'border-white bg-white text-(--color-navy-700)' : 'border-(--color-line-strong)'}`}>
        {on ? '✓' : ''}
      </span>
      {children}
    </button>
  )
}

/** A titled block of a page, on its own surface: one card per thing, so each reads apart. */
export function Panel({ title, aside, children, className = '', bodyClassName = '' }: {
  title?: ReactNode
  aside?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={`flex flex-col rounded-md border border-(--color-line) bg-(--color-surface) ${className}`}>
      {(title || aside) && (
        <div className="flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-(--color-line) px-4 py-2">
          {typeof title === 'string' ? <h3 className="eyebrow">{title}</h3> : title}
          {aside}
        </div>
      )}
      <div className={`flex flex-col gap-3 px-4 py-3 ${bodyClassName}`}>{children}</div>
    </section>
  )
}

/** The rules of a screen, at its foot: read once, then out of the way. */
export function InfoStrip({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 gap-3 border-t border-(--color-line) bg-(--color-surface-2) px-5 py-3 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
      <span aria-hidden className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-(--color-navy-100) text-(length:--text-micro) font-bold text-(--color-navy-700)">i</span>
      <span>{children}</span>
    </div>
  )
}

/** A yes, no or not-yet mark in a round chip: the state reads before the words do. */
export function StateDot({ ok }: { ok: boolean | null }) {
  return (
    <span aria-hidden className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-(length:--text-micro) font-bold leading-none ${
      ok === true ? 'bg-(--color-good)/12 text-(--color-good)'
        : ok === false ? 'bg-(--color-overdue)/10 text-(--color-overdue)'
          : 'bg-(--color-surface-3) text-(--color-ink-4)'}`}>
      {ok === true ? '✓' : ok === false ? '✕' : '–'}
    </span>
  )
}

/** The answer a block gives, before its reasons: a tinted strip with a mark. */
export function Verdict({ ok, tone, title, children }: { ok: boolean | null; tone?: 'privileged' | 'warn'; title: string; children?: ReactNode }) {
  const look = tone === 'privileged' ? 'border-(--color-privileged)/30 bg-(--color-privileged)/6'
    : tone === 'warn' ? 'border-(--color-warn)/30 bg-(--color-warn)/6'
      : ok === true ? 'border-(--color-good)/30 bg-(--color-good)/6'
        : ok === false ? 'border-(--color-overdue)/30 bg-(--color-overdue)/6'
          : 'border-(--color-line-strong) bg-(--color-surface-3)'
  return (
    <div className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${look}`}>
      <StateDot ok={ok} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-(length:--text-base) font-semibold text-(--color-ink)">{title}</span>
        {children && <span className="text-(length:--text-small) text-(--color-ink-2)">{children}</span>}
      </div>
    </div>
  )
}

/** One fact in a small tile: its name, its value, and where the value comes from. */
export function Fact({ label, value, from }: { label: string; value: ReactNode; from?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2">
      <span className="eyebrow">{label}</span>
      <span className="text-(length:--text-small) font-semibold text-(--color-ink)">{value}</span>
      {from && <span className="text-(length:--text-micro) text-(--color-ink-3)">{from}</span>}
    </div>
  )
}

/** A labelled fact in a header line (inside a <dl>): "Company Redachem Gulf · R002". */
export function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5">
      <dt className="text-(--color-ink-4)">{label}</dt>
      <dd className="min-w-0 truncate text-(--color-ink-2)" title={value}>{value}</dd>
    </div>
  )
}

/**
 * What approving this person makes true, for the confirmation (owner, 27 Oct 2026:
 * "add confirmation for approval"): what they will open, their column groups, hierarchy and
 * phone, that the record keeps the problems shown, and anything that still keeps them
 * out or widens their rows. Consequences, not the button's name (ConfirmDialog).
 */
export function ApprovalConsequences({ card }: { card: AccessApprovalCard }) {
  const p = card.person!
  const name = p.displayName ?? `Mindzer user ${p.mindzerUserId}`
  const reports = [...new Map(card.sees.map((s) => [s.reportSlug, s])).values()].map((s) => {
    const lines = card.sees.filter((x) => x.reportSlug === s.reportSlug)
    // A report the code list does not count comes back with no count (052 "SEES"),
    // never 0: summed as 0 it would read "reaches nothing" (D62's first new report).
    if (lines.every((x) => x.rowsRecent == null)) return `${s.displayName} (rows not counted)`
    const rows = lines.reduce((n, x) => n + (x.rowsRecent ?? 0), 0)
    return `${s.displayName} (${rows.toLocaleString('en-GB')} ${rows === 1 ? 'row' : 'rows'} in the last 12 months)`
  })
  const groups = cardGroups(card)
  const n = card.warnings.length
  return (
    <div className="flex flex-col gap-2.5 text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
      <p>
        {p.roleSetUp && !p.blocksEntry
          ? <><span className="font-semibold text-(--color-ink)">{name}</span> can enter MBI {p.signedIn ? 'at their next request' : 'when they first sign in'}.</>
          : <><span className="font-semibold text-(--color-ink)">{name}</span> is approved, but still cannot enter: {!p.roleSetUp ? 'their HR Role is not configured in MBI.' : 'a problem marked Cannot enter must be fixed first.'}</>}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2">
        <dt className="text-(--color-ink-3)">Opens</dt>
        <dd className="text-(--color-ink)">{reports.length ? listWords(reports) : 'no report'}</dd>
        <dt className="text-(--color-ink-3)">Column groups</dt>
        <dd className="text-(--color-ink)">{groups.length ? groups.join('; ') : 'none'}</dd>
        <dt className="text-(--color-ink-3)">Hierarchy</dt>
        <dd className="text-(--color-ink)">{HIERARCHY[p.hierarchyMode] ?? 'Off'} · phone {p.mobileAllowed ? 'allowed' : 'not allowed'}</dd>
      </dl>
      {n > 0 && <p>The approval records the {n} {n === 1 ? 'problem' : 'problems'} shown now, and your name.</p>}
      {p.widens && (
        <p className="rounded-md border border-(--color-overdue)/30 bg-(--color-overdue)/6 px-3 py-2 font-semibold text-(--color-overdue)">
          ⚠ This widens their rows: they would see rows that are probably not theirs.
        </p>
      )}
    </div>
  )
}

/**
 * The column groups an approval card gives, one entry a report: "Sales: Cost, Margin".
 * A group counts only on a line of its own report (D53), so the card carries them per
 * line; lines of one report in different scopes add up.
 */
export function cardGroups(card: AccessApprovalCard): string[] {
  const byReport = new Map<string, { name: string; groups: Set<string> }>()
  for (const s of card.sees) {
    if (!s.columnGroups) continue
    const r = byReport.get(s.reportSlug) ?? { name: s.displayName, groups: new Set<string>() }
    s.columnGroups.split(', ').forEach((g) => r.groups.add(g))
    byReport.set(s.reportSlug, r)
  }
  return [...byReport.values()].map((r) => `${r.name}: ${[...r.groups].join(', ')}`)
}

/** A key on the keyboard, as a shortcut hint shows it. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-grid min-w-5 place-items-center rounded border border-(--color-line-strong) bg-(--color-surface) px-1 py-px font-sans text-(length:--text-micro) font-semibold leading-4 text-(--color-ink-2) shadow-[0_1px_0_var(--color-line-strong)]">
      {children}
    </kbd>
  )
}

/** A person's initials in a round badge, as PersonCell draws them, at the page's size. */
export function Initials({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
  const dims = { sm: 'h-7 w-7 text-(length:--text-micro)', md: 'h-9 w-9 text-(length:--text-small)', lg: 'h-11 w-11 text-(length:--text-base)' }[size]
  return (
    <span aria-hidden className={`grid shrink-0 place-items-center rounded-full bg-(--color-navy-100) font-semibold text-(--color-navy-700) ${dims}`}>
      {initials}
    </span>
  )
}

/** A unit or team as Mindzer names it, with its D365 code, or the warning that it has none. */
export function PlaceChip({ name, code, struck = false, children }: { name: string; code: string | null; struck?: boolean; children?: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-(length:--text-small) ${
      code ? 'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink)' : 'border-(--color-warn)/30 bg-(--color-warn)/6 text-(--color-ink)'
    } ${struck ? 'line-through opacity-60' : ''}`}>
      {name}
      {code ? <span className="text-(--color-ink-4)">{code}</span> : <span className="text-(--color-warn)">no D365 code · gives nothing</span>}
      {children}
    </span>
  )
}

/** The time of day of an instant, 24-hour. */
export function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/** A day as a heading: "Today", "Yesterday", or "Mon 27 Oct 2026". */
export function dayHeading(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((start(today) - start(d)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}

/** "3 of 5 on | All | None", for a list of choices. */
function AllNone({ on, total, onAll, onNone, label }: { on: number; total: number; onAll: () => void; onNone: () => void; label: string }) {
  return (
    <span className="flex shrink-0 items-center gap-2 text-(length:--text-small)">
      <span className="text-(--color-ink-3) tabular-nums">{on} of {total} on</span>
      <span aria-hidden className="h-3.5 w-px bg-(--color-line-strong)" />
      <button type="button" disabled={on === total} onClick={onAll} aria-label={`All ${label}`}
        className="rounded px-1 font-medium text-(--color-navy-700) hover:underline disabled:cursor-default disabled:text-(--color-ink-4) disabled:no-underline">All</button>
      <button type="button" disabled={on === 0} onClick={onNone} aria-label={`None of the ${label}`}
        className="rounded px-1 font-medium text-(--color-navy-700) hover:underline disabled:cursor-default disabled:text-(--color-ink-4) disabled:no-underline">None</button>
    </span>
  )
}

/**
 * REPORTS, EACH WITH ITS COLUMN GROUPS (owner, 29 Oct 2026). One report per row; ticking a report
 * opens its card on its own column groups, and unticking it closes the card. A ticked report with
 * none says so. Used wherever reports and column groups are given together: an HR role and an MBI
 * additional scope. A group goes only with its own report (D53): the caller's onToggleReport takes
 * a report's groups off with it, as before.
 */
export function ReportGroups({ reports, groups, chosenReports, chosenGroups, onToggleReport, onSetReports, onToggleGroup, onSetGroups, reportChanged, groupChanged }: {
  reports: { code: string; label: string }[]
  groups: {
    code: string; label: string; reportSlug: string; hint?: string
    /** The whole text on hover, when the hint is cut short. */
    title?: string
  }[]
  chosenReports: string[]
  chosenGroups: string[]
  onToggleReport: (code: string) => void
  onSetReports: (codes: string[]) => void
  onToggleGroup: (code: string) => void
  /** One report's groups at once: its All and None; the others' stay as they are. */
  onSetGroups: (report: string, codes: string[]) => void
  /** Whether a report or group differs from what was saved, to mark it Added or Removed. */
  reportChanged?: (code: string) => boolean
  groupChanged?: (code: string) => boolean
}) {
  const reportsOn = reports.filter((r) => chosenReports.includes(r.code)).length
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        {/* A heading: the section has no panel title above it (review of 19l). */}
        <h3 className="eyebrow">Reports</h3>
        <AllNone on={reportsOn} total={reports.length} label="reports"
          onAll={() => onSetReports(reports.map((r) => r.code))} onNone={() => onSetReports([])} />
      </div>
      <ul className="flex flex-col gap-1.5">
        {reports.map((r) => {
          const on = chosenReports.includes(r.code)
          const mine = groups.filter((g) => g.reportSlug === r.code)
          const mineOn = mine.filter((g) => chosenGroups.includes(g.code)).length
          return (
            <li key={r.code} className={`rounded-md border transition-colors ${
              on ? 'border-(--color-navy-700) bg-(--color-surface)' : 'border-(--color-line-strong) bg-(--color-surface) hover:border-(--color-navy-300)'}`}>
              <button type="button" role="checkbox" aria-checked={on} aria-expanded={on} onClick={() => onToggleReport(r.code)}
                className={`flex min-h-10 w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-(length:--text-small) ${
                  on ? 'bg-(--color-navy-50) font-medium text-(--color-ink)' : 'text-(--color-ink-2) hover:bg-(--color-navy-50)'}`}>
                <span aria-hidden className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border text-(length:--text-micro) font-bold leading-none ${
                  on ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white' : 'border-(--color-ink-4) bg-(--color-surface)'}`}>
                  {on ? '✓' : ''}
                </span>
                <span className="min-w-0 flex-1">{r.label}</span>
                {reportChanged?.(r.code) && <ChangedMark on={on} />}
                <span aria-hidden className={`shrink-0 text-(--color-ink-3) transition-transform motion-reduce:transition-none ${on ? 'rotate-90' : ''}`}>›</span>
              </button>
              {on && (
                <div className="anim-rise flex flex-col gap-2 border-t border-(--color-line) px-3 py-2.5 pl-9">
                  {mine.length === 0 ? (
                    <p className="text-(length:--text-small) text-(--color-ink-3)">No column groups yet. They are made on Column definition.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <span className="eyebrow">Column groups</span>
                        <AllNone on={mineOn} total={mine.length} label={`${r.label} column groups`}
                          onAll={() => onSetGroups(r.code, mine.map((g) => g.code))} onNone={() => onSetGroups(r.code, [])} />
                      </div>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-1.5">
                        {mine.map((g) => (
                          <CheckTile key={g.code} on={chosenGroups.includes(g.code)} onChange={() => onToggleGroup(g.code)}
                            hint={g.hint} title={g.title} changed={groupChanged?.(g.code)}>
                            {g.label}
                          </CheckTile>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** One choice of several: a tile with a square box that fills when it is on. */
export function CheckTile({ on, onChange, hint, title, changed = false, children }: {
  on: boolean; onChange: (on: boolean) => void; hint?: string; title?: string; changed?: boolean; children: ReactNode
}) {
  return (
    <button
      title={title}
      type="button"
      role="checkbox"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`flex min-h-9 items-center gap-2.5 rounded-md border px-3 py-1.5 text-left text-(length:--text-small) transition-colors ${
        on
          ? 'border-(--color-navy-700) bg-(--color-navy-50) font-medium text-(--color-ink)'
          : 'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
      }`}
    >
      <span aria-hidden className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border text-(length:--text-micro) font-bold leading-none transition-colors ${
        on ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white' : 'border-(--color-ink-4) bg-(--color-surface)'}`}>
        {on ? '✓' : ''}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span>{children}</span>
        {hint && <span className="text-(length:--text-micro) font-normal text-(--color-ink-3)">{hint}</span>}
      </span>
      {changed && <ChangedMark on={on} />}
    </button>
  )
}

/** An on/off setting as a switch, with what it means beside it. */
export function Switch({ on, onChange, label, detail, changed = false }: {
  on: boolean; onChange: (on: boolean) => void; label: string; detail?: string; changed?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      // The name without its state: aria-checked already says on or off. The
      // changed mark is said too, as CheckTile's is.
      aria-label={`${label}${detail ? `. ${detail}` : ''}${changed ? `. ${on ? 'Added' : 'Removed'}, not saved yet` : ''}`}
      onClick={() => onChange(!on)}
      className={`flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
        on ? 'border-(--color-navy-700) bg-(--color-navy-50)' : 'border-(--color-line-strong) bg-(--color-surface) hover:border-(--color-navy-300)'}`}
    >
      <span aria-hidden className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-(--color-navy-700)' : 'bg-(--color-ink-4)'}`}>
        <span className={`absolute h-4 w-4 rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-(length:--text-small) font-semibold text-(--color-ink)">{label}: {on ? 'on' : 'off'}</span>
        {detail && <span className="text-(length:--text-small) text-(--color-ink-3)">{detail}</span>}
      </span>
      {changed && <ChangedMark on={on} />}
    </button>
  )
}

/**
 * A setting changed on screen and not saved yet, said as what it does: Added (navy) when
 * it is now on, Removed (red) when it is now off (owner, 29 Oct 2026: not "changed").
 */
export function ChangedMark({ on = true, className = '' }: { on?: boolean; className?: string }) {
  return (
    <span className={`shrink-0 rounded-full px-1.5 text-(length:--text-micro) font-semibold leading-4 text-white ${
      on ? 'bg-(--color-navy-700)' : 'bg-(--color-overdue)'} ${className}`} title={`${on ? 'Added' : 'Removed'}, not saved yet`}>
      {on ? 'Added' : 'Removed'}
    </span>
  )
}
